import type { components } from '@connected/api-client/api.generated';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import { toPascalCase } from '@connected/api-client/lib/utils';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Label } from '@connected/form-fields/components/label';
import { SwitchField } from '@connected/form-fields/components/switch';
import {
    Button,
} from '@connected/ui/components/button';
import {
    Input,
} from '@connected/ui/components/input';
import {
    Popout,
    PopoutBody,
    PopoutContent,
    PopoutFooter,
    PopoutHeader,
    PopoutTitle,
    PopoutTrigger,
} from '@connected/ui/components/popout';
import { useForm } from '@tanstack/react-form';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { Pen, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InformationLine } from '@/components/information-line';
import { useFormTouched } from '@/hooks/use-form-touched';
import { queryClient } from '@/lib/query-client';
import { getFormErrors, placeFormErrors } from '@/lib/utils';
import { showConfirm } from '@/stores/confirmer';

type ContactPerson = {
    firstName: string;
    middleName: string;
    lastName: string;
    department: string;
    jobTitleDescription: string;
    email: string;
    mobilePhoneNumber: string;
    roleInSoftware: string[];
    isResponsibleForPowerSystems: boolean;
};

type Organization
    = components['schemas']['ListElectricalProjectOrganizationQueryResultOrganization'];

export function ContactPersonsList({
    electricalProjectVersionId,
    organization,
    disabled = false,
}: {
    electricalProjectVersionId: string;
    organization: Organization;
    disabled?: boolean;
}) {
    const { t } = useTranslation('applications');

    const contactPersons = organization.ContactPersons || [];

    const deleteContactPersonMutation = api.useMutation(
        'delete',
        '/api/v2/electrical/projects/contactpersons/delete',
        {
            onMutate: (variables) => {
                const queryOption = elektraAppQueryOptions.project.request.organizationOptions({
                    electricalProjectVersionId,
                });

                const previousData = queryClient.getQueryData<components['schemas']['ListElectricalProjectOrganizationQueryResult']>(
                    queryOption.queryKey,
                );

                queryClient.cancelQueries(queryOption);

                const newData: components['schemas']['ListElectricalProjectOrganizationQueryResult'] = {
                    ...previousData,
                    Organizations: previousData?.Organizations?.map((org) => {
                        if (org.ElectricalProjectOrganizationId === organization.ElectricalProjectOrganizationId) {
                            return {
                                ...org,
                                ContactPersons: org.ContactPersons?.filter(x => x.ElectricalProjectContactPersonId !== variables.params.query.ElectricalProjectContactPersonId),
                            };
                        }
                        return org;
                    }) || [],
                };

                queryClient.setQueryData<components['schemas']['ListElectricalProjectOrganizationQueryResult']>(
                    queryOption.queryKey,
                    newData,
                );
            },
            onSuccess: () => {
                queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.request.organizationOptions({ electricalProjectVersionId }),
                );
            },
            scope: {
                id: electricalProjectVersionId,
            },
        },
    );

    async function handleDelete({
        contactPersonId,
    }: {
        contactPersonId: string;
    }) {
        if (
            await showConfirm(
                t(
                    'those-involved.client.form.contactpersons.delete.are-you-sure',
                ),
            )
        ) {
            deleteContactPersonMutation.mutate({
                params: {
                    query: {
                        ElectricalProjectContactPersonId: contactPersonId,
                    },
                },
            });

            deleteContactPersonMutation.reset();
        }
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col items-center justify-between">
                <h3 className="text-base font-bold">
                    {t('those-involved.client.form.contactpersons.title')}
                </h3>

                <AddContactPersonModal
                    electricalProjectVersionId={electricalProjectVersionId}
                    electricalProjectOrganizationId={organization.ElectricalProjectOrganizationId}
                    disabled={disabled}
                    isPVInstallationConstructionCompany={organization.IsPVInstallationConstructionCompany || false}
                />
            </div>
            <div className="flex flex-col gap-6">
                {contactPersons?.map(contactPerson => (
                    <div
                        key={contactPerson.ElectricalProjectContactPersonId}
                        className="flex flex-col gap-4 rounded-lg border border-border p-4"
                    >
                        <div className="flex items-center justify-between">
                            <h4 className="font-bold flex justify-start items-center gap-2">
                                {contactPerson.FirstName}
                                {' '}
                                {contactPerson.MiddleName}
                                {' '}
                                {contactPerson.LastName}
                                {contactPerson.IsResponsibleForPowerSystem && (
                                    <span className="ml-2 text-xs font-normal text-green-500">
                                        (Installatieverantwoordelijke)
                                    </span>
                                )}
                            </h4>
                            <div className="flex items-center gap-4">
                                <EditContactPersonModal
                                    electricalProjectVersionId={electricalProjectVersionId}
                                    contactPerson={contactPerson}
                                    organizationId={organization.ElectricalProjectOrganizationId}
                                    disabled={disabled}
                                    isPVInstallationConstructionCompany={organization.IsPVInstallationConstructionCompany || false}
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() =>
                                        handleDelete({
                                            contactPersonId:
                                                contactPerson.ElectricalProjectContactPersonId!,
                                        })}
                                    disabled={disabled}

                                >
                                    <Trash2 />
                                </Button>
                            </div>
                        </div>
                        <div className="flex flex-col gap-1">
                            <InformationLine
                                label={t(
                                    'those-involved.client.form.contactpersons.department',
                                )}
                                value={
                                    contactPerson.Department || 'Niet bekend'
                                }
                            />
                            <InformationLine
                                label={t(
                                    'those-involved.client.form.contactpersons.role',
                                )}
                                value={
                                    contactPerson.JobTitleDescription
                                    || 'Niet bekend'
                                }
                            />
                            <InformationLine
                                label={t(
                                    'those-involved.client.form.contactpersons.email',
                                )}
                                value={contactPerson.Email || 'Niet bekend'} // TODO: Fix
                            />
                            <InformationLine
                                label={t(
                                    'those-involved.client.form.contactpersons.mobile_phonenumber',
                                )}
                                value={
                                    contactPerson.MobilePhoneNumber
                                    || 'Niet bekend'
                                } // TODO: Fix
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function AddContactPersonModal({
    electricalProjectVersionId,
    electricalProjectOrganizationId,
    disabled = false,
    isPVInstallationConstructionCompany = false,
}: {
    electricalProjectVersionId: string;
    electricalProjectOrganizationId: string;
    disabled?: boolean;
    isPVInstallationConstructionCompany?: boolean;
}) {
    const { t } = useTranslation('applications');
    const { t: c } = useTranslation('common');

    const [isOpen, setIsOpen] = useState(false);

    const contactpersonsMutation = api.useMutation(
        'post',
        '/api/v2/electrical/projects/contactpersons/create',
        {
            onMutate: (variables) => {
                const body = toPascalCase(variables?.body)!;

                const queryOption = elektraAppQueryOptions.project.request.organizationOptions({
                    electricalProjectVersionId,
                });

                const previousData = queryClient.getQueryData<components['schemas']['ListElectricalProjectOrganizationQueryResult']>(
                    queryOption.queryKey,
                );

                queryClient.cancelQueries(queryOption);

                const newData: components['schemas']['ListElectricalProjectOrganizationQueryResult'] = {
                    ...previousData,
                    Organizations: previousData?.Organizations?.map((org) => {
                        if (org.ElectricalProjectOrganizationId === electricalProjectOrganizationId) {
                            return {
                                ...org,
                                ContactPersons: [
                                    ...(org.ContactPersons || []),
                                    body,
                                ],
                            };
                        }
                        return org;
                    }) || [],
                };

                queryClient.setQueryData<components['schemas']['ListElectricalProjectOrganizationQueryResult']>(
                    queryOption.queryKey,
                    newData,
                );
            },
            onSuccess: () => {
                queryClient.invalidateQueries(elektraAppQueryOptions.project.request.organizationOptions({ electricalProjectVersionId }));
            },
            onError: (error) => {
                const errors = getFormErrors<ContactPerson>(error);
                if (errors.length > 0) {
                    // eslint-disable-next-line ts/no-use-before-define
                    placeFormErrors<ContactPerson>({ fieldErrors: errors, formApi: form });
                }
                console.error('Error creating contact person:', errors);
            },
            scope: {
                id: electricalProjectVersionId,
            },
        },
    );

    const defaultValues: ContactPerson = {
        firstName: '',
        middleName: '',
        lastName: '',
        department: '',
        jobTitleDescription: '',
        email: '',
        mobilePhoneNumber: '',
        roleInSoftware: [],
        isResponsibleForPowerSystems: false,
    };

    const form = useForm<ContactPerson, ZodValidator>(
        {
            defaultValues,
            validatorAdapter: zodValidator(),
            onSubmit: ({ value }) => {
                contactpersonsMutation.mutate({
                    body: {
                        ElectricalProjectContactPersonId: crypto.randomUUID(),
                        ElectricalProjectOrganizationId:
                            electricalProjectOrganizationId,
                        FirstName: value.firstName,
                        MiddleName: value.middleName,
                        LastName: value.lastName,
                        Department: value.department,
                        JobTitleDescription: value.jobTitleDescription,
                        Email: value.email === '' ? null : value.email,
                        MobilePhoneNumber: value.mobilePhoneNumber,
                        IsResponsibleForPowerSystem: value.isResponsibleForPowerSystems,
                    },
                });

                setIsOpen(false);
                form.reset();
                contactpersonsMutation.reset();
            },
        },
    );

    const isFormTouched = useFormTouched(form.store, defaultValues);

    async function handleClose() {
        if (isFormTouched) {
            if (await showConfirm(c('messages.confirmClose'))) {
                setIsOpen(false);
                form.reset();
                contactpersonsMutation.reset();
            }
        }
        else {
            setIsOpen(false);
            form.reset();
            contactpersonsMutation.reset();
        }
    }

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <Button variant="link" onClick={() => setIsOpen(true)} disabled={disabled}>{c('actions.general.add')}</Button>
            <PopoutContent>
                <PopoutHeader>
                    <PopoutTitle>
                        {t(
                            'those-involved.client.form.contactpersons.modal.title',
                        )}
                    </PopoutTitle>
                </PopoutHeader>
                <PopoutBody>
                    <div
                        className="flex flex-col gap-4"
                    >
                        <div className="grid grid-cols-7 gap-2">
                            <form.Field
                                name="firstName"
                                validators={{
                                    onChange:
                                        validators.createElectricalProjectContactPersonCommand.firstName,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label
                                        label={t(
                                            'those-involved.client.form.contactpersons.modal.form.first_name.label',
                                        )}
                                        className="col-span-5"
                                    >
                                        <Input
                                            name={name}
                                            onChange={e =>
                                                handleChange(e.target.value)}
                                            value={state.value}
                                            placeholder={t(
                                                'those-involved.client.form.contactpersons.modal.form.first_name.placeholder',
                                            )}
                                            disabled={disabled}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </form.Field>

                            <form.Field
                                name="middleName"
                                validators={{
                                    onChange:
                                        validators.createElectricalProjectContactPersonCommand.middleName,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label
                                        label={t(
                                            'those-involved.client.form.contactpersons.modal.form.infix.label',
                                        )}
                                        className="col-span-2"
                                    >
                                        <Input
                                            name={name}
                                            onChange={e =>
                                                handleChange(e.target.value)}
                                            value={state.value}
                                            placeholder={t(
                                                'those-involved.client.form.contactpersons.modal.form.infix.placeholder',
                                            )}
                                            disabled={disabled}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </form.Field>
                        </div>

                        <form.Field
                            name="lastName"
                            validators={{
                                onChange:
                                    validators.createElectricalProjectContactPersonCommand.lastName,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label
                                    label={t(
                                        'those-involved.client.form.contactpersons.modal.form.last_name.label',
                                    )}
                                >
                                    <Input
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t(
                                            'those-involved.client.form.contactpersons.modal.form.last_name.placeholder',
                                        )}
                                        disabled={disabled}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </form.Field>

                        <form.Field
                            name="department"
                            validators={{
                                onChange:
                                    validators.createElectricalProjectContactPersonCommand.department,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label
                                    label={t(
                                        'those-involved.client.form.contactpersons.modal.form.department.label',
                                    )}
                                >
                                    <Input
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t(
                                            'those-involved.client.form.contactpersons.modal.form.department.placeholder',
                                        )}
                                        disabled={disabled}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </form.Field>

                        <form.Field
                            name="jobTitleDescription"
                            validators={{
                                onChange:
                                    validators.createElectricalProjectContactPersonCommand.jobTitleDescription,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label
                                    label={t(
                                        'those-involved.client.form.contactpersons.modal.form.role.label',
                                    )}
                                >
                                    <Input
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t(
                                            'those-involved.client.form.contactpersons.modal.form.role.placeholder',
                                        )}
                                        disabled={disabled}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </form.Field>

                        <form.Field
                            name="email"
                            validators={{
                                onChange: validators.createElectricalProjectContactPersonCommand.email,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label
                                    label={t(
                                        'those-involved.client.form.contactpersons.modal.form.email.label',
                                    )}
                                >
                                    <Input
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t(
                                            'those-involved.client.form.contactpersons.modal.form.email.placeholder',
                                        )}
                                        disabled={disabled}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </form.Field>

                        <form.Field
                            name="mobilePhoneNumber"
                            validators={{
                                onChange:
                                    validators.createElectricalProjectContactPersonCommand.mobilePhoneNumber,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label
                                    label={t(
                                        'those-involved.client.form.contactpersons.modal.form.mobile_phonenumber.label',
                                    )}
                                >
                                    <Input
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t(
                                            'those-involved.client.form.contactpersons.modal.form.mobile_phonenumber.placeholder',
                                        )}
                                        disabled={disabled}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </form.Field>
                        {
                            !isPVInstallationConstructionCompany && (
                                <form.Field
                                    name="isResponsibleForPowerSystems"
                                    validators={{
                                        onChange:
                                            validators.editElectricalProjectContactPersonCommand.isResponsibleForPowerSystem,
                                    }}
                                >
                                    {({ handleChange, state, name }) => (
                                        <>
                                            <SwitchField
                                                name={name}
                                                handleChange={handleChange}
                                                value={state.value}
                                                label="Is installatieverantwoordelijke"
                                                disabled={disabled}
                                            />
                                            <FieldInfo state={state} />
                                        </>
                                    )}
                                </form.Field>
                            )
                        }
                    </div>
                </PopoutBody>
                <PopoutFooter>
                    <div className="flex w-full flex-col gap-4">
                        <div className="flex w-full gap-4">
                            <Button
                                className="w-full"
                                variant="outline"
                                disabled={disabled}
                                onClick={handleClose}
                            >
                                {c('actions.general.cancel')}
                            </Button>
                            <Button
                                className="w-full"
                                disabled={disabled}
                                onClick={form.handleSubmit}
                            >
                                {c('actions.general.save')}
                            </Button>
                        </div>
                        {form.state.errors.length > 0 && (
                            <em className="text-red-500">
                                {form.state.errors.join(', ')}
                            </em>
                        )}
                    </div>
                </PopoutFooter>
            </PopoutContent>
        </Popout>
    );
}

function EditContactPersonModal({
    electricalProjectVersionId,
    contactPerson,
    disabled,
    organizationId,
    isPVInstallationConstructionCompany = false,
}: {
    electricalProjectVersionId: string;
    contactPerson: components['schemas']['ListElectricalProjectOrganizationQueryResultContact'];
    disabled: boolean;
    organizationId: string;
    isPVInstallationConstructionCompany?: boolean;
}) {
    const { t } = useTranslation('applications');
    const { t: c } = useTranslation('common');

    const [isOpen, setIsOpen] = useState(false);

    const contactPersonMutation = api.useMutation(
        'patch',
        '/api/v2/electrical/projects/contactpersons/patch',
        {
            onMutate: (variables) => {
                const body = toPascalCase(variables?.body)!;

                const queryOption = elektraAppQueryOptions.project.request.organizationOptions({
                    electricalProjectVersionId,
                });

                const previousData = queryClient.getQueryData<components['schemas']['ListElectricalProjectOrganizationQueryResult']>(
                    queryOption.queryKey,
                );

                queryClient.cancelQueries(queryOption);

                const newData: components['schemas']['ListElectricalProjectOrganizationQueryResult'] = {
                    ...previousData,
                    Organizations: previousData?.Organizations?.map((org) => {
                        if (org.ElectricalProjectOrganizationId === organizationId) {
                            return {
                                ...org,
                                ContactPersons: org.ContactPersons?.map((x) => {
                                    if (x.ElectricalProjectContactPersonId === contactPerson.ElectricalProjectContactPersonId) {
                                        return body;
                                    }
                                    return x;
                                }),
                            };
                        }
                        return org;
                    }) || [],
                };

                queryClient.setQueryData<components['schemas']['ListElectricalProjectOrganizationQueryResult']>(
                    queryOption.queryKey,
                    newData,
                );
            },
            onSuccess: () => {
                queryClient.invalidateQueries(elektraAppQueryOptions.project.request.organizationOptions({
                    electricalProjectVersionId,
                }));
            },
            onError: (error) => {
                const errors = getFormErrors<ContactPerson>(error);
                if (errors.length > 0) {
                    // eslint-disable-next-line ts/no-use-before-define
                    placeFormErrors<ContactPerson>({ fieldErrors: errors, formApi: form });
                }
                console.error('Error creating contact person:', errors);
            },
            scope: {
                id: electricalProjectVersionId,
            },
        },
    );

    const defaultValues: ContactPerson = {
        firstName: contactPerson?.FirstName || '',
        middleName: contactPerson?.MiddleName || '',
        lastName: contactPerson?.LastName || '',
        department: contactPerson?.Department || '',
        jobTitleDescription: contactPerson?.JobTitleDescription || '',
        email: contactPerson?.Email || '',
        mobilePhoneNumber: contactPerson?.MobilePhoneNumber || '',
        roleInSoftware: [],
        isResponsibleForPowerSystems: contactPerson?.IsResponsibleForPowerSystem ?? false,
    };

    const form = useForm<
        ContactPerson,
        ZodValidator
    >({
        defaultValues,
        validatorAdapter: zodValidator(),
        onSubmit: ({ value }) => {
            const mutationBody: components['schemas']['PatchElectricalProjectContactPersonCommand'] = {
                ElectricalProjectContactPersonId: contactPerson.ElectricalProjectContactPersonId!,
                FirstName: value.firstName,
                MiddleName: value.middleName,
                LastName: value.lastName,
                Department: value.department,
                JobTitleDescription: value.jobTitleDescription,
                Email: value.email === '' ? null : value.email,
                MobilePhoneNumber: value.mobilePhoneNumber,
                IsResponsibleForPowerSystem: value.isResponsibleForPowerSystems,
            };

            contactPersonMutation.mutate({
                body: mutationBody,
            });

            setIsOpen(false);
            form.reset();
            contactPersonMutation.reset();
        },
    });

    const isFormTouched = useFormTouched(form.store, defaultValues);

    async function handleClose() {
        if (isFormTouched) {
            if (await showConfirm(c('messages.confirmClose'))) {
                setIsOpen(false);
                form.reset();
                contactPersonMutation.reset();
            }
        }
        else {
            setIsOpen(false);
            form.reset();
            contactPersonMutation.reset();
        }
    }

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <PopoutTrigger asChild>
                <Button variant="ghost" disabled={disabled}>
                    <Pen />
                </Button>
            </PopoutTrigger>
            <PopoutContent>
                <PopoutHeader>
                    <PopoutTitle>
                        {t(
                            'those-involved.client.form.contactpersons.edit-modal.title',
                        )}
                    </PopoutTitle>
                </PopoutHeader>
                <PopoutBody>
                    <div
                        className="flex flex-col gap-4 py-6"
                    >
                        <div className="grid grid-cols-7 gap-2">
                            <form.Field
                                name="firstName"
                                validators={{
                                    onChange:
                                        validators.editElectricalProjectContactPersonCommand.firstName,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label
                                        label={t(
                                            'those-involved.client.form.contactpersons.modal.form.first_name.label',
                                        )}
                                        className="col-span-5"
                                    >
                                        <Input
                                            name={name}
                                            onChange={e =>
                                                handleChange(e.target.value)}
                                            value={state.value}
                                            placeholder={t(
                                                'those-involved.client.form.contactpersons.modal.form.first_name.placeholder',
                                            )}
                                            disabled={disabled}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </form.Field>

                            <form.Field
                                name="middleName"
                                validators={{
                                    onChange:
                                        validators.editElectricalProjectContactPersonCommand.middleName,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label
                                        label={t(
                                            'those-involved.client.form.contactpersons.modal.form.infix.label',
                                        )}
                                        className="col-span-2"
                                    >
                                        <Input
                                            name={name}
                                            onChange={e =>
                                                handleChange(e.target.value)}
                                            value={state.value}
                                            placeholder={t(
                                                'those-involved.client.form.contactpersons.modal.form.infix.placeholder',
                                            )}
                                            disabled={disabled}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </form.Field>
                        </div>

                        <form.Field
                            name="lastName"
                            validators={{
                                onChange:
                                    validators.editElectricalProjectContactPersonCommand.lastName,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label
                                    label={t(
                                        'those-involved.client.form.contactpersons.modal.form.last_name.label',
                                    )}
                                >
                                    <Input
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t(
                                            'those-involved.client.form.contactpersons.modal.form.last_name.placeholder',
                                        )}
                                        disabled={disabled}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </form.Field>

                        <form.Field
                            name="department"
                            validators={{
                                onChange:
                                    validators.editElectricalProjectContactPersonCommand.department,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label
                                    label={t(
                                        'those-involved.client.form.contactpersons.modal.form.department.label',
                                    )}
                                >
                                    <Input
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t(
                                            'those-involved.client.form.contactpersons.modal.form.department.placeholder',
                                        )}
                                        disabled={disabled}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </form.Field>

                        <form.Field
                            name="jobTitleDescription"
                            validators={{
                                onChange:
                                    validators.editElectricalProjectContactPersonCommand.jobTitleDescription,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label
                                    label={t(
                                        'those-involved.client.form.contactpersons.modal.form.role.label',
                                    )}
                                >
                                    <Input
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t(
                                            'those-involved.client.form.contactpersons.modal.form.role.placeholder',
                                        )}
                                        disabled={disabled}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </form.Field>

                        <form.Field
                            name="email"
                            validators={{
                                onChange:
                                    validators.editElectricalProjectContactPersonCommand.email,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label
                                    label={t(
                                        'those-involved.client.form.contactpersons.modal.form.email.label',
                                    )}
                                >
                                    <Input
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t(
                                            'those-involved.client.form.contactpersons.modal.form.email.placeholder',
                                        )}
                                        disabled={disabled}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </form.Field>

                        <form.Field
                            name="mobilePhoneNumber"
                            validators={{
                                onChange:
                                    validators.editElectricalProjectContactPersonCommand.mobilePhoneNumber,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label
                                    label={t(
                                        'those-involved.client.form.contactpersons.modal.form.mobile_phonenumber.label',
                                    )}
                                >
                                    <Input
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t(
                                            'those-involved.client.form.contactpersons.modal.form.mobile_phonenumber.placeholder',
                                        )}
                                        disabled={disabled}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </form.Field>
                        {!isPVInstallationConstructionCompany && (
                            <form.Field
                                name="isResponsibleForPowerSystems"
                                validators={{
                                    onChange:
                                        validators.editElectricalProjectContactPersonCommand.isResponsibleForPowerSystem,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <>
                                        <SwitchField
                                            name={name}
                                            handleChange={handleChange}
                                            value={state.value}
                                            label="Is installatieverantwoordelijke"
                                            disabled={disabled}
                                        />
                                        <FieldInfo state={state} />
                                    </>
                                )}
                            </form.Field>
                        )}
                    </div>
                </PopoutBody>
                <PopoutFooter>
                    <div className="flex w-full flex-col gap-4">
                        <div className="flex w-full gap-4">
                            <Button
                                className="w-full"
                                onClick={handleClose}
                                variant="outline"
                                disabled={disabled}
                            >
                                {c('actions.general.cancel')}
                            </Button>
                            <Button
                                className="w-full"
                                onClick={form.handleSubmit}
                                disabled={disabled}
                            >
                                {c('actions.general.save')}
                            </Button>
                        </div>
                        {form.state.errors.length > 0 && (
                            <em className="text-red-500">
                                {form.state.errors.join(', ')}
                            </em>
                        )}
                    </div>

                </PopoutFooter>
            </PopoutContent>
        </Popout>
    );
}
